import { isDefined } from '@aloft/toolkit-shared';

import mapboxgl from 'mapbox-gl';
import { useEffect } from 'react';

import { MAP_LAYER_DEFINITIONS, MAP_SOURCES } from './constants.js';
import { Coordinates, UseMapProps } from './types.js';

const subjectSource = (center: Coordinates): mapboxgl.GeoJSONSourceRaw => ({
  type: 'geojson',
  data: {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [center.lng, center.lat],
    },
    properties: {},
  },
});

type Props<Feature> = {
  mapRef: UseMapProps<Feature>['mapRef'];
  subject: Coordinates | undefined;
  center: Coordinates;
  callbacks: {
    onMapLoad?: () => void;
    onStyleLoad?: () => void;
  };
};

export function useSubjectLayerCallback<Feature>({
  mapRef,
  subject,
  center,
  callbacks,
}: Props<Feature>) {
  const handleLoadSubject = (e: mapboxgl.MapboxEvent<undefined> & mapboxgl.EventData) => {
    const map = e.target;
    if (!isDefined(map) || !isDefined(subject)) return;

    callbacks.onMapLoad?.();

    if (!map.getSource(MAP_SOURCES.SUBJECT)) {
      map.addSource(MAP_SOURCES.SUBJECT, subjectSource(center));
    }

    if (!map.getLayer(MAP_LAYER_DEFINITIONS.SUBJECT.id)) {
      map.addLayer(MAP_LAYER_DEFINITIONS.SUBJECT);
    }
  };

  const handleLoadStyles = (e: mapboxgl.MapboxEvent<undefined> & mapboxgl.EventData) => {
    const map = e.target;
    if (!isDefined(map) || !isDefined(subject)) return;

    callbacks.onStyleLoad?.();

    if (!map.getSource(MAP_SOURCES.SUBJECT)) {
      map.addSource(MAP_SOURCES.SUBJECT, subjectSource(center));
    }

    if (!map.getLayer(MAP_LAYER_DEFINITIONS.SUBJECT.id)) {
      map.addLayer(MAP_LAYER_DEFINITIONS.SUBJECT);
    }
  };

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!isDefined(map) || !isDefined(subject)) return;

    map.on('load', handleLoadSubject);
    map.on('style.load', handleLoadStyles);

    return () => {
      map.off('load', handleLoadSubject);
      map.off('style.load', handleLoadStyles);
    };
  }, [callbacks, mapRef, subject, center]);
}
